import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import AuthenticationService from "service/auth/AuthenticationService";

const MAX_BACKOFF_MS = 60000;
const MAX_TICKET_FAILURES = 3;

// A stream that has gone quiet for longer than this is treated as dead even if
// the browser still reports it OPEN. A half-dead connection behind a CDN looks
// identical to an idle one, and the server heartbeats every 25s, so silence
// this long means something in between has stopped forwarding.
const WATCHDOG_MS = 90000;

/**
 * Owns the single EventSource carrying live call events.
 *
 * Ported from expert-dashboard-frontend so Bridge reads the same stream.
 * Deliberately framework-free so the connection is not tied to any component's
 * lifecycle: the LTR in-call pill outlives the page that placed the call.
 *
 * SparkTG is subscribed to ring and hang-up events only, so an event's state
 * is "ongoing" (ringing or talking -- indistinguishable) or "ended". Events of
 * an LTR call also carry `ltrCallId` and, on a ring, `ringing` ("caller" or
 * "patient": whose phone is ringing).
 *
 * Subscribers receive {type: "call", call}, {type: "snapshot", call} on every
 * (re)connect, and {type: "unavailable"} once the stream has given up.
 */
class CallEventStream {
  constructor() {
    this.apiService = new APIService();
    this.authenticationService = new AuthenticationService();

    this.eventSource = null;
    this.subscribers = new Set();
    this.refCount = 0;
    this.attempt = 0;
    // Consecutive ticket refusals. Bridge employees may not be served by the
    // stream at all; after a few refusals it stops asking, and callers fall
    // back to their manual controls.
    this.ticketFailures = 0;
    // True once the stream has given up for this user; reset by start().
    this.unavailable = false;
    this.reconnectTimer = null;
    this.watchdogTimer = null;
    this.lastMessageAt = 0;
    this.stopped = true;
  }

  /**
   * Begin streaming. Reference counted, so several mounts share one connection.
   */
  start() {
    this.refCount += 1;
    if (this.refCount > 1) return;

    this.stopped = false;
    this.attempt = 0;
    this.ticketFailures = 0;
    this.unavailable = false;
    this._connect();
  }

  /**
   * Release a reference. The connection closes when the last one goes.
   */
  stop() {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount > 0) return;

    this.stopped = true;
    this._teardown();
  }

  /**
   * Listen for events. Returns an unsubscribe function -- always call it.
   */
  subscribe(handler) {
    this.subscribers.add(handler);
    return () => this.subscribers.delete(handler);
  }

  /**
   * Fetch the doctor's currently ongoing call.
   *
   * This is what restores the floater after a reload, so it runs on every
   * successful connect rather than only at startup.
   */
  fetchActiveCall(onResponse) {
    this.apiService.getAgain(
      APIConfig.callSparktg.activeCall,
      (data) => onResponse(data && data.call ? data.call : null),
      () => onResponse(null),
    );
  }

  _emit(event) {
    this.subscribers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.error("call event subscriber threw", error);
      }
    });
  }

  _connect() {
    if (this.stopped) return;
    if (!this.authenticationService.isLoggedIn()) return;

    // EventSource cannot set headers, so authenticate by minting a short-lived
    // ticket over a normal (header-authenticated) request first.
    const refused = () => {
      this.ticketFailures += 1;
      if (this.ticketFailures >= MAX_TICKET_FAILURES) {
        console.warn("call event stream unavailable for this user; not retrying");
        this._teardown();
        this.unavailable = true;
        this._emit({ type: "unavailable" });
        return;
      }
      this._scheduleReconnect();
    };
    this.apiService.postAgain(
      APIConfig.callSparktg.streamTicket,
      {},
      (data) => {
        if (this.stopped) return;
        if (!data || !data.ticket) {
          refused();
          return;
        }
        this.ticketFailures = 0;
        this._open(data.ticket);
      },
      refused,
    );
  }

  _open(ticket) {
    this._closeEventSource();

    const url = `${APIConfig.callSparktg.stream}?ticket=${encodeURIComponent(
      ticket,
    )}`;

    let source;
    try {
      // No withCredentials: the ticket in the query string is the whole
      // authentication, and asking for credentials would additionally require
      // the ingress to echo an exact Access-Control-Allow-Origin rather than
      // the wildcard it serves today.
      source = new EventSource(url);
    } catch (error) {
      console.error("failed to open call event stream", error);
      this._scheduleReconnect();
      return;
    }

    this.eventSource = source;
    this.lastMessageAt = Date.now();

    source.onopen = () => {
      this.attempt = 0;
      this.lastMessageAt = Date.now();
      this.fetchActiveCall((call) => {
        this._emit({ type: "snapshot", call });
      });
    };

    source.addEventListener("call", (message) => {
      this.lastMessageAt = Date.now();
      try {
        this._emit({ type: "call", call: JSON.parse(message.data) });
      } catch (error) {
        console.error("unparseable call event", error, message.data);
      }
    });

    // Heartbeat comments do not fire a listener, but any traffic resets the
    // browser's own idea of the connection; the watchdog polls readyState.
    source.onmessage = () => {
      this.lastMessageAt = Date.now();
    };

    source.onerror = () => {
      // EventSource retries transport drops on its own, but gives up entirely
      // on an HTTP error (a rejected ticket, say) -- that surfaces as CLOSED.
      // Only then do we need to re-mint and reopen.
      if (source.readyState === EventSource.CLOSED) {
        this._scheduleReconnect();
      }
    };

    this._startWatchdog();
  }

  _startWatchdog() {
    this._clearWatchdog();
    this.watchdogTimer = setInterval(() => {
      if (this.stopped) return;
      if (Date.now() - this.lastMessageAt < WATCHDOG_MS) return;

      console.warn("call event stream went silent; reconnecting");
      this._scheduleReconnect();
    }, WATCHDOG_MS / 3);
  }

  _clearWatchdog() {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  _scheduleReconnect() {
    if (this.stopped) return;
    if (this.reconnectTimer) return;

    this._closeEventSource();
    this._clearWatchdog();

    this.attempt = Math.min(this.attempt + 1, 6);
    const base = Math.min(MAX_BACKOFF_MS, 1000 * Math.pow(2, this.attempt));
    const wait = base / 2 + Math.random() * (base / 2);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._connect();
    }, wait);
  }

  _closeEventSource() {
    if (!this.eventSource) return;
    this.eventSource.onopen = null;
    this.eventSource.onerror = null;
    this.eventSource.onmessage = null;
    this.eventSource.close();
    this.eventSource = null;
  }

  _teardown() {
    this._closeEventSource();
    this._clearWatchdog();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

const callEventStream = new CallEventStream();

// Dev-only hook so the call flow can be exercised from the console:
//   window.__pushCallEvent({state: "ended", callId: "t1"})
if (process.env.NODE_ENV !== "production") {
  window.__pushCallEvent = (call) =>
    callEventStream._emit({ type: "call", call });
}

export default callEventStream;
